import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Film, Sparkles, Wand2, Layers, AlertCircle, Clock, X, Stars, Play, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import PublishToCommunityButton from "../components/PublishToCommunityButton";
import VideoPlayerDialog from "../components/VideoPlayerDialog";
import { iosSave } from "../lib/iosSave";
import { ExportToPlatformButton } from "../components/WaveTwoFeatures";
import { AssistantChat } from "../components/AssistantChat";
import { errMsg } from "../lib/errMsg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EFFECT_ICONS = {
  prompt_edit:    Wand2,
  frame_stylize:  Sparkles,
  filter_overlay: Layers,
};

const CGIStudioPage = () => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [effects, setEffects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sources, setSources] = useState({ images: [], videos: [] });
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedEffect, setSelectedEffect] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [styleId, setStyleId] = useState(null);
  const [filterId, setFilterId] = useState(null);
  // Pass 5 — CGI music polish picker. Mirrors the AI Video Generator UX:
  // "music" plays a genre-routed song, "brand_only" overlays brand+CTA with
  // Fal.ai's native audio, "raw" skips polish entirely (default).
  const [polishMode, setPolishMode] = useState("raw");
  const [musicGenre, setMusicGenre] = useState("");
  const [voiceoverText, setVoiceoverText] = useState("");
  const [voiceoverArchetype, setVoiceoverArchetype] = useState("narrator_male");
  const [submitting, setSubmitting] = useState(false);
  const [player, setPlayer] = useState(null); // {url, title} when playing a render in-app

  // Heavy renders (per-frame video stylize / prompt_edit on a video source)
  // can take several minutes. Show a friendly banner + a one-time confirm
  // so the user knows they can leave and we'll email them.
  const isHeavyVideoJob = useMemo(() => {
    if (!selectedSource || !selectedEffect) return false;
    if (selectedSource.kind !== "video") return false;
    return selectedEffect.id === "frame_stylize" || selectedEffect.id === "prompt_edit";
  }, [selectedSource, selectedEffect]);

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    (async () => {
      try {
        const [fx, t, src] = await Promise.all([
          axios.get(`${API}/cgi/effects`,           { headers: auth }),
          axios.get(`${API}/cgi/tasks`,             { headers: auth }),
          axios.get(`${API}/admin/moodboard/available`, { headers: auth }).catch(() => ({ data: { images: [], videos: [] } })),
        ]);
        setEffects(fx.data.effects || []);
        setTasks(t.data.items || []);
        setSources(src.data || { images: [], videos: [] });
      } catch (e) {
        toast.error("Could not load CGI studio.");
      }
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  // Poll task progress every 8s while any task is still queued/processing
  useEffect(() => {
    const inFlight = tasks.some(t => t.status === "queued" || t.status === "processing");
    if (!inFlight) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/cgi/tasks`, { headers: auth });
        setTasks(r.data.items || []);
      } catch (_e) { /* poll silently */ }
    }, 8000);
    return () => clearInterval(id);
  }, [tasks, auth]);

  const submit = async () => {
    if (!selectedSource) {
      toast.error("Pick a source from your XEA generations.");
      return;
    }
    if (!selectedEffect) {
      toast.error("Pick an effect.");
      return;
    }
    if (isHeavyVideoJob) {
      const ok = window.confirm(
        "Heads up — stylizing every frame of a video can take several minutes (longer for long clips). " +
        "You can safely close this browser tab; we'll email you the moment your render is ready. " +
        "\n\nContinue?"
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const body = {
        source_collection: selectedSource.collection,
        source_id:         selectedSource.id,
        effect:            selectedEffect.id,
      };
      if (selectedEffect.id === "prompt_edit")    body.prompt = prompt.trim();
      if (selectedEffect.id === "frame_stylize")  body.style  = styleId;
      if (selectedEffect.id === "filter_overlay") body.filter = filterId;
      // Pass 5 — CGI music polish settings (only relevant if source is video)
      if (selectedSource?.kind === "video") {
        body.polish_mode = polishMode;
        body.music_genre = polishMode === "music" ? (musicGenre.trim() || null) : null;
      }
      // Pass 6 — Voiceover overlay (post-render mux). Skipped for image effects.
      if (voiceoverText.trim() && selectedSource?.kind === "video") {
        body.voiceover_text = voiceoverText.trim().slice(0, 500);
        body.voiceover_archetype = voiceoverArchetype;
      }

      const r = await axios.post(`${API}/cgi/start`, body, { headers: auth });
      setTasks((prev) => [r.data.task, ...prev]);
      toast.success("Queued. We'll email you when it's ready 🎬");
      setSelectedEffect(null);
      setPrompt(""); setStyleId(null); setFilterId(null);
      setPolishMode("raw"); setMusicGenre("");
      setVoiceoverText(""); setVoiceoverArchetype("narrator_male");
    } catch (e) {
      toast.error(errMsg(e, "Could not queue render."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 py-8 space-y-8"
      data-testid="cgi-studio-page"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-blue-500 flex items-center justify-center shadow-md">
          <Film className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-500">
            CGI Studio · Beta
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-ink-950 tracking-tight">
            Movie-grade effects on your generations.
          </h1>
        </div>
      </div>

      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          To keep CGI Studio 100% copyright-clean, you can only edit media you generated inside XEA
          (Marketing or Designer mode). Uploads are disabled here on purpose.
        </p>
      </div>

      {/* Step 1 — Pick a source */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
          Step 1 · Source
        </p>
        {sources.images.length + sources.videos.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-ink-200 p-8 text-center bg-white text-sm text-ink-500">
            No generations yet — head to Designer or Marketing mode to create something, then come back.
          </div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2">
            {[...sources.videos, ...sources.images].slice(0, 24).map((s) => {
              const key = `${s.collection}:${s.id}`;
              const chosen = selectedSource && selectedSource.id === s.id && selectedSource.collection === s.collection;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedSource(s)}
                  data-testid={`cgi-source-${s.id}`}
                  className={`relative rounded-2xl overflow-hidden border-2 aspect-square transition-all ${
                    chosen ? "border-blue-500 ring-2 ring-blue-200" : "border-ink-200 hover:border-ink-950"
                  }`}
                >
                  {s.kind === "video" ? (
                    <video src={s.media_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  ) : (
                    <img src={s.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  )}
                  {chosen && (
                    <span className="absolute top-1 right-1 bg-blue-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full">
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Step 2 — Pick an effect */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
          Step 2 · Effect
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {effects.map((fx) => {
            const Icon = EFFECT_ICONS[fx.id] || Sparkles;
            const chosen = selectedEffect?.id === fx.id;
            // Fal.ai-paused gating: when fx.disabled_for includes the
            // selected source's kind, gray the card out and block clicks.
            const isDisabledForSource =
              fx.disabled_for &&
              selectedSource &&
              fx.disabled_for.includes(selectedSource.kind);
            return (
              <button
                key={fx.id}
                type="button"
                disabled={isDisabledForSource}
                onClick={() => !isDisabledForSource && setSelectedEffect(fx)}
                data-testid={`cgi-effect-${fx.id}`}
                title={isDisabledForSource ? fx.disabled_reason : undefined}
                className={`text-left p-5 rounded-3xl border-2 transition-all relative ${
                  isDisabledForSource
                    ? "border-ink-200 bg-ink-50 opacity-60 cursor-not-allowed"
                    : chosen
                    ? "border-blue-500 bg-gradient-to-br from-blue-500/10 to-violet-500/10"
                    : "border-ink-200 bg-white hover:border-ink-950"
                }`}
              >
                {isDisabledForSource && (
                  <span
                    className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-[0.10em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                    data-testid={`cgi-effect-${fx.id}-disabled-badge`}
                  >
                    ⏳ Paused
                  </span>
                )}
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-lg font-bold text-ink-950">{fx.label}</p>
                <p className="text-xs text-ink-500 mt-1 leading-relaxed">{fx.blurb}</p>
                {isDisabledForSource && (
                  <p className="text-[10px] text-amber-700 mt-2 leading-tight">
                    {fx.disabled_reason}
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Signpost — direct users to Movie Mode when they want from-nothing
            generation. FX Lab transforms existing media; Movie Mode generates
            from scratch with character portraits as Kling start-frames. */}
        <Link
          to="/dashboard/movie"
          data-testid="cgi-movie-mode-signpost"
          className="mt-4 flex items-center gap-3 p-4 rounded-2xl border border-fuchsia-200 bg-gradient-to-r from-fuchsia-50/80 to-violet-50/80 hover:from-fuchsia-100 hover:to-violet-100 transition-colors group"
        >
          <div className="shrink-0 w-10 h-10 rounded-full bg-fuchsia-500/10 border border-fuchsia-300 flex items-center justify-center">
            <Stars className="w-5 h-5 text-fuchsia-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-ink-950 leading-tight">
              Want to generate a clip from a character portrait?
            </p>
            <p className="text-xs text-ink-600 mt-0.5">
              Movie Mode renders from nothing — write the idea, get back a short film with consistent characters.
            </p>
          </div>
          <span
            className="shrink-0 text-xs font-black uppercase tracking-[0.18em] text-fuchsia-700 group-hover:translate-x-0.5 transition-transform"
            aria-hidden="true"
          >
            Try it →
          </span>
        </Link>
      </section>

      {/* Step 3 — Effect-specific params */}
      {selectedEffect && (
        <section className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
            Step 3 · Tune {selectedEffect.label}
          </p>

          {isHeavyVideoJob && (
            <div
              data-testid="cgi-heavy-warning"
              className="rounded-2xl bg-blue-50 border border-blue-200 p-4 flex gap-3"
            >
              <Clock className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900 leading-relaxed">
                <strong className="block mb-1">Per-frame video render — this takes time.</strong>
                Each frame is restyled by AI, so expect several minutes (longer for long clips).
                <span className="block mt-1 text-blue-700">
                  You can safely close the browser — we&apos;ll email you the moment the render is ready.
                </span>
              </div>
            </div>
          )}

          {selectedEffect.id === "prompt_edit" && (
            <div className="space-y-2">
              {/* AI assistant — emits 📹 VIDEO PROMPT marker. CGI clips are
                  a fixed 5s so we ignore the duration recommendation here
                  and only pull the prompt body. */}
              <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/40 to-white p-3">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-600 mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Ask XEA for a CGI prompt
                </p>
                <AssistantChat
                  variant="embed"
                  contextHint="Describe the effect you want — re-light, slow-mo, lens flare, time-warp…"
                  usePromptLabel="Use as CGI prompt"
                  onUsePrompt={(text) => {
                    const m = text.match(
                      /📹\s*\*?\*?VIDEO PROMPT\*?\*?\s*·\s*Recommended:\s*\d+\s*s\s*\n+([\s\S]+?)(?:\n+✨|\n\n[A-Z]|$)/i,
                    );
                    const body = (m ? m[1] : text).trim();
                    setPrompt(body.slice(0, 600));
                    toast.success("Prompt copied into the editor below");
                  }}
                />
              </div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                maxLength={600}
                placeholder='e.g. "re-light to golden hour, add lens flare, slow-mo dust"'
                data-testid="cgi-prompt"
                className="w-full p-3 rounded-xl border border-ink-200 focus:outline-none focus:border-blue-500 text-sm resize-none"
              />
              <DirectorPolishButton
                prompt={prompt}
                effect={selectedEffect?.id}
                onPolished={(text) => {
                  setPrompt(text.slice(0, 600));
                  toast.success("Polished by Director ✨");
                }}
              />
            </div>
          )}

          {selectedEffect.id === "frame_stylize" && (
            <div className="flex flex-wrap gap-2">
              {(selectedEffect.styles || []).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStyleId(s)}
                  data-testid={`cgi-style-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                    styleId === s ? "bg-blue-500 text-white border-blue-500" : "bg-white border-ink-200"
                  }`}
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          )}

          {selectedEffect.id === "filter_overlay" && (
            <div className="flex flex-wrap gap-2">
              {(selectedEffect.filters || []).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilterId(f)}
                  data-testid={`cgi-filter-${f}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                    filterId === f ? "bg-blue-500 text-white border-blue-500" : "bg-white border-ink-200"
                  }`}
                >
                  {f.replace("_", " ")}
                </button>
              ))}
            </div>
          )}

          {/* Pass 5 — Music polish picker (video sources only) */}
          {selectedSource?.kind === "video" && (
            <div className="space-y-3 pt-3 border-t border-ink-100">
              <p className="text-xs uppercase tracking-[0.10em] font-bold text-ink-500 flex items-center gap-2">
                🎵 How should we finish the video?
              </p>
              <div className="grid grid-cols-3 gap-2" data-testid="cgi-polish-mode-picker">
                {[
                  { id: "music",      label: "🎵 With song",  sub: "Add a genre-routed song afterward" },
                  { id: "brand_only", label: "🎤 Brand only", sub: "Brand + CTA overlay · no music" },
                  { id: "raw",        label: "📹 Raw",        sub: "Just the edit · no polish" },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPolishMode(m.id)}
                    data-testid={`cgi-polish-mode-${m.id}`}
                    aria-pressed={polishMode === m.id}
                    className={`p-2.5 rounded-xl border text-left transition ${
                      polishMode === m.id
                        ? "border-blue-500 bg-blue-50 ring-2 ring-blue-300"
                        : "border-ink-200 bg-white hover:border-ink-300"
                    }`}
                  >
                    <div className="text-xs font-semibold text-ink-950">{m.label}</div>
                    <div className="text-[10px] text-ink-500 mt-0.5 leading-tight">{m.sub}</div>
                  </button>
                ))}
              </div>
              {polishMode === "music" && (
                <div className="space-y-1">
                  <input
                    type="text"
                    value={musicGenre}
                    onChange={(e) => setMusicGenre(e.target.value.slice(0, 120))}
                    placeholder="Genre / vibe — e.g. 'lo-fi study beats', 'cinematic synthwave'"
                    className="w-full bg-ink-100 border border-ink-200 rounded-lg px-3 py-2 text-sm text-ink-950 placeholder:text-ink-400 focus:border-blue-400 focus:outline-none"
                    maxLength={120}
                    data-testid="cgi-music-genre-input"
                  />
                  <p className="text-[10px] text-ink-500">Leave blank to auto-pick from the bundled library.</p>
                </div>
              )}
            </div>
          )}

          {/* Voiceover overlay (Pass 6) — only available when source is video */}
          {selectedSource?.kind === "video" && (
            <div className="space-y-2 p-3 rounded-xl border border-fuchsia-200 bg-fuchsia-50/50" data-testid="cgi-voiceover-block">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-fuchsia-700">
                  Voiceover · ElevenLabs
                </p>
                {voiceoverText && (
                  <span className="text-[10px] text-fuchsia-700 font-medium">
                    {voiceoverText.length}/500
                  </span>
                )}
              </div>
              <textarea
                value={voiceoverText}
                onChange={(e) => setVoiceoverText(e.target.value.slice(0, 500))}
                rows={2}
                maxLength={500}
                placeholder="Optional: narration to overlay (leave blank to skip)"
                data-testid="cgi-voiceover-text"
                className="w-full p-2 rounded-lg border border-fuchsia-200 focus:outline-none focus:border-fuchsia-500 text-sm resize-none bg-white"
              />
              {voiceoverText.trim() && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-500 block mb-1">
                    Voice archetype
                  </label>
                  <select
                    value={voiceoverArchetype}
                    onChange={(e) => setVoiceoverArchetype(e.target.value)}
                    data-testid="cgi-voiceover-archetype"
                    className="w-full bg-white border border-fuchsia-200 rounded-lg px-3 py-2 text-sm text-ink-950 focus:border-fuchsia-500 focus:outline-none"
                  >
                    <option value="narrator_male">Narrator · warm male</option>
                    <option value="narrator_female">Narrator · documentary female</option>
                    <option value="protagonist_male">Protagonist · young male hero</option>
                    <option value="protagonist_female">Protagonist · young female hero</option>
                    <option value="antagonist_male">Antagonist · gravelly villain</option>
                    <option value="antagonist_female">Antagonist · icy calculating</option>
                    <option value="elder_male">Elder · wise low timbre</option>
                    <option value="elder_female">Elder · grandmotherly</option>
                    <option value="child">Child · youthful bright</option>
                    <option value="generic_male">Neutral male</option>
                    <option value="generic_female">Neutral female</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={submit}
            disabled={submitting || !selectedSource}
            data-testid="cgi-submit"
            className="w-full bg-blue-500 hover:bg-blue-600 text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
            Queue render
          </Button>
        </section>
      )}

      {/* Render queue */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
          Your renders
        </p>
        {tasks.length === 0 ? (
          <p className="text-sm text-ink-500">Nothing queued yet.</p>
        ) : (
          <div className="space-y-2">
            {tasks.map((t) => {
              const inFlight = t.status === "queued" || t.status === "processing";
              const pct = Math.max(0, Math.min(100, Number(t.progress || 0)));

              const cancel = async () => {
                if (!window.confirm("Cancel this render? It can't be resumed.")) return;
                try {
                  await axios.post(`${API}/cgi/tasks/${t.id}/cancel`, {}, { headers: auth });
                  setTasks(prev => prev.map(x => x.id === t.id
                    ? { ...x, status: "cancelled", progress_label: "Cancelled by user" }
                    : x));
                  toast.success("Render cancelled");
                } catch (e) {
                  toast.error(errMsg(e, "Could not cancel."));
                }
              };

              return (
                <div
                  key={t.id}
                  data-testid={`cgi-task-${t.id}`}
                  className="bg-white border border-ink-200 rounded-2xl px-4 py-3 flex flex-col gap-2"
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${
                          t.status === "completed"
                            ? "bg-emerald-100 text-emerald-700"
                            : t.status === "failed"
                            ? "bg-rose-100 text-rose-700"
                            : t.status === "cancelled"
                            ? "bg-ink-100 text-ink-500"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {t.status}
                      </span>
                      <span className="text-sm font-bold text-ink-950 truncate">
                        {t.effect.replace("_", " ")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      {inFlight && (
                        <>
                          <span
                            data-testid={`cgi-task-${t.id}-pct`}
                            className="text-[11px] font-bold text-blue-500 tabular-nums"
                          >
                            {pct}%
                          </span>
                          <button
                            type="button"
                            onClick={cancel}
                            data-testid={`cgi-task-${t.id}-cancel`}
                            className="text-[11px] font-bold text-rose-500 hover:text-rose-700 inline-flex items-center gap-0.5"
                          >
                            <X className="w-3 h-3" /> cancel
                          </button>
                        </>
                      )}
                      {t.result_url && t.status === "completed" && (() => {
                        const isVideo =
                          /\.(mp4|webm|mov)(\?|$)/i.test(t.result_url) ||
                          t.result_url.startsWith("data:video");
                        const label = t.effect.replace("_", " ");
                        return (
                        <>
                          {isVideo ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="!h-7 !px-2 !text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => setPlayer({ url: t.result_url, title: label })}
                              data-testid={`cgi-task-${t.id}-play`}
                            >
                              <Play className="w-3 h-3 mr-1" /> Play
                            </Button>
                          ) : (
                            <a
                              href={t.result_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`cgi-task-${t.id}-result`}
                              className="text-[11px] font-bold text-emerald-700 underline"
                            >
                              open
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="!h-7 !px-2 !text-[11px] border-ink-300 text-ink-700 hover:bg-ink-50"
                            onClick={() => iosSave(t.result_url, {
                              kind: isVideo ? "video" : "image",
                              filename: `xea-fx-${t.id.slice(0, 8)}.${isVideo ? "mp4" : "jpg"}`,
                            })}
                            data-testid={`cgi-task-${t.id}-download`}
                          >
                            <Download className="w-3 h-3 mr-1" /> Download
                          </Button>
                          <PublishToCommunityButton
                            sourceCollection="cgi_render_tasks"
                            sourceId={t.id}
                            testIdPrefix={`cgi-task-${t.id}-publish`}
                            className="!h-7 !px-2 !text-[11px]"
                          />
                          <ExportToPlatformButton
                            sourceCollection="cgi_render_tasks"
                            sourceId={t.id}
                            testIdPrefix={`cgi-task-${t.id}-export`}
                          />
                        </>
                        );
                      })()}
                      <span className="text-[11px] text-ink-500">
                        {new Date(t.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  {inFlight && (
                    <div className="flex flex-col gap-1" data-testid={`cgi-task-${t.id}-progress`}>
                      <div className="h-1.5 bg-ink-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      {t.progress_label && (
                        <span className="text-[10px] text-ink-500">
                          {t.progress_label}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <VideoPlayerDialog
        open={!!player}
        onClose={() => setPlayer(null)}
        url={player?.url}
        title={player?.title}
      />
    </div>
  );
};

export default CGIStudioPage;

/**
 * Director Polish — one-click Claude Sonnet 4.5 cinematic prompt upgrade.
 *
 * Sits underneath the prompt textarea. Sends the current prompt to
 * `/api/cgi/director-polish` and replaces the field with a director-grade
 * cinematic rewrite (camera move, lens language, lighting terminology).
 * Same model that powers the Movie Mode planner, single-shot for FX Lab.
 */
const DirectorPolishButton = ({ prompt, effect, onPolished }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    if (!prompt || prompt.trim().length < 4) {
      toast.error("Type a prompt first — at least a few words.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/cgi/director-polish`,
        { prompt: prompt.trim(), effect },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.polished_prompt) {
        onPolished(r.data.polished_prompt);
      } else {
        toast.error("Director returned an empty polish.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Director is busy — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      disabled={loading}
      data-testid="cgi-director-polish-btn"
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-fuchsia-300 bg-gradient-to-r from-fuchsia-50 to-violet-50 hover:from-fuchsia-100 hover:to-violet-100 text-fuchsia-700 font-bold text-xs uppercase tracking-[0.18em] transition-colors disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Stars className="w-3.5 h-3.5" />
      )}
      {loading ? "Polishing..." : "Polish with Director (Claude Sonnet 4.5)"}
    </button>
  );
};

